'use client';
import { useRouter } from 'next/navigation';
import useAffirmScript from '~/scripts/affirm-script';
import Image from 'next/image';
import React, { useCallback, useMemo } from 'react';
import SpinningLoader from '~/components/spinning-loader';
import {
  IPaymentConnector,
  PaymentConnectorPostMessageType,
} from '~/interfaces/payment';
import { useOrderInprogress } from '~/hooks/use-order-inprogress';
import {
  AffirmAPIConfig,
  AffirmCheckoutObject,
  Discounts,
} from '~/interfaces/affirm/affirm';
import { affirmMerchantName } from '~/constant/constants';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { generateProductUrl } from '~/utils/utils';
import { BuyNowPayLaterAffirmLeasing } from '~/gql/__generated__/types';
import {
  affirmPlanId,
  normalizeCheckoutCart,
} from '~/modules/payment/affirm/utils/misc';
import Styles from './affirm-buttons.module.scss';
import { submitPayment } from '~/utils/payment/submit-visa-order';
import { useSetSyfOverlay, useBillingAddressState } from '~/store/wmap-store';
import { WMAP_PREFIX } from '~/modules/payment/constants';
import { sendAffirmApplyNowUtag } from '~/utils/tealium';

interface IAffirmProps {
  checkoutCart: ISalesOrderResponse;
  affirmLeasingConfiguration: BuyNowPayLaterAffirmLeasing;
  acceptContent: IPaymentConnector;
  cartId: string;
  isPaymentBtn?: boolean;
  isDisabled?: boolean;
}

interface AffirmCheckoutSuccess {
  created: string;
  checkout_token: string;
}

const formatCheckoutTotal = (total: number): number => {
  return total % 1 !== 0 ? Math.round(total * 100) : total * 100;
};

const AffirmCheckout: React.FC<IAffirmProps> = ({
  checkoutCart,
  affirmLeasingConfiguration,
  acceptContent,
  cartId,
  isPaymentBtn,
  isDisabled,
}) => {
  const router = useRouter();
  const { orderInProgress, setOrderInProgress, setAffirmDialogOpen } =
    useOrderInprogress();
  const billingAddressState = useBillingAddressState();
  const acceptPageContentString = acceptContent.connectorHtml.AcceptPageContent;
  const acceptPageContent = useMemo(() => {
    return JSON.parse(acceptPageContentString) as AffirmAPIConfig;
  }, [acceptPageContentString]);

  const isAffirmScriptLoaded = useAffirmScript(
    acceptPageContent.EnvironmentURL,
    acceptPageContent.APIKey,
  );
  const { shipping, items, tax, total, email } =
    normalizeCheckoutCart(checkoutCart) || {};
  const filteredProducts = (items ?? []).reduce<{
    products: AffirmCheckoutObject['items'];
    discounts: Discounts;
  }>(
    (acc, item) => {
      if (item.Qty > 0) {
        acc.products.push({
          display_name: item.Description || '',
          sku: item.ItemId,
          unit_price: (item.Price || 0) * 100,
          qty: item.Qty,
          item_image_url: item.ProductImageUrl || '',
          item_url: generateProductUrl(item) || '',
          categories: [[item.Segment || '', item.ProductType || '']],
        });
      } else if (item.Qty < 0) {
        const index = Object.keys(acc.discounts).length;

        acc.discounts[`RETURN_ADJUSTMENT_${index}`] = {
          discount_amount: Math.round(Math.abs(item.NetAmount) * 100),
          discount_display_name:
            item.Description || `Return Adjustment ${index + 1}`,
        };
      }
      return acc;
    },
    {
      products: [],
      discounts: {},
    },
  );
  const checkoutObject: AffirmCheckoutObject = {
    merchant: {
      user_confirmation_url: '',
      user_cancel_url: '',
      user_confirmation_url_action: 'POST',
      public_api_key: acceptPageContent.APIKey,
      name: affirmMerchantName,
    },

    shipping: {
      name: {
        first:
          shipping?.firstName || billingAddressState?.firstName?.value || '',
        last: shipping?.lastName || billingAddressState?.lastName?.value || '',
      },
      address: {
        line1:
          shipping?.addressLine || billingAddressState?.address?.value || '',
        line2:
          shipping?.addressLine2 || billingAddressState?.apartment?.value || '',
        city: shipping?.addressCity || billingAddressState?.city?.value || '',
        state:
          shipping?.addressState || billingAddressState?.state?.value || '',
        zipcode:
          shipping?.addressZip || billingAddressState?.zipcode?.value || '',
        country: 'USA',
      },
      email: email || billingAddressState?.emailAddress?.value || '',
      phone_number:
        shipping?.phone || billingAddressState?.phoneNumber?.value || '',
    },
    items: filteredProducts.products,
    ...(Object.keys(filteredProducts.discounts).length > 0
      ? { discounts: filteredProducts.discounts }
      : {}),
    metadata: { mode: 'modal' },

    tax_amount: formatCheckoutTotal(tax || 0),
    total: formatCheckoutTotal(total || 0),
  };

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue =
      'Are you sure you want to leave? Your checkout may be lost.';
  }, []);
  const setSyfOverlay = useSetSyfOverlay();
  const handleAffirmCheckout = () => {
    if (
      !window.affirm ||
      !window.affirm.checkout ||
      typeof window.affirm.checkout.open !== 'function' ||
      !isAffirmScriptLoaded
    ) {
      console.error('Affirm script is not ready yet.');
      return;
    }
    setSyfOverlay(true);
    setOrderInProgress(true);

    if (!checkoutObject) {
      console.error('Checkout object is not ready.');
      return;
    }
    sendAffirmApplyNowUtag?.();

    window.affirm.checkout(checkoutObject);
    window.affirm.checkout.open({
      onOpen: () => {
        console.info('Affirm iframe opened');
        setAffirmDialogOpen(true);
        setSyfOverlay(true);
        setOrderInProgress(false);
        window.addEventListener('beforeunload', handleBeforeUnload);
      },
      onClose: () => {
        console.info('Affirm iframe closed');
        setOrderInProgress(false);
        setAffirmDialogOpen(false);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        setSyfOverlay(false);
      },
      onFail: (error: unknown) => {
        console.error('Affirm Checkout Failed:', error);
        setOrderInProgress(false);
        setAffirmDialogOpen(false);
        setSyfOverlay(false);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      },
      onSuccess: async (checkout: AffirmCheckoutSuccess) => {
        setOrderInProgress(true);
        setSyfOverlay(true);
        console.info('Affirm Checkout Successful:', checkout);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        // using a small trick to do reusability by mimic the post message event from iframe to reuse the visa submit order flow
        const syncEvent: MessageEvent = {
          data: JSON.stringify({
            type: PaymentConnectorPostMessageType.Result,
            value: checkout.checkout_token ?? '',
            id: acceptContent.connectorHtml.PaymentConnectorId,
          }),
        } as MessageEvent<never>;

        submitPayment(
          false,
          syncEvent,
          cartId,
          checkoutCart,
          router,
          setSyfOverlay,
          affirmLeasingConfiguration.affirmLeasingPlanId ?? affirmPlanId,
        );
      },
      onValidationError: (error: unknown) => {
        console.error('Validation Error:', error);
        setOrderInProgress(false);
        setAffirmDialogOpen(false);
        setSyfOverlay(false);
      },
    });
  };

  return (
    <div className={Styles.wrapper}>
      <button
        type="button"
        id={`${WMAP_PREFIX}-affirm-checkout-${isPaymentBtn ? 'p2' : 'p1'}`}
        className={Styles.affirm_button}
        onClick={() => {
          void handleAffirmCheckout();
        }}
        disabled={orderInProgress || isDisabled}
      >
        {orderInProgress ? (
          <SpinningLoader height="22px" width="22px" color="#1b244d" />
        ) : (
          <Image
            src="/makeapayment/icons/payment-connector/affirm.svg"
            alt="Affirm payment option logo"
            height={22}
            width={56}
          />
        )}
      </button>
      <p tabIndex={0}>{affirmLeasingConfiguration.affirmDisclaimerText}</p>
    </div>
  );
};

export default AffirmCheckout;
